import React from 'react';
import { Tile as TileType } from './types';
import { getAllTiles } from './gameLogic';
import { GRID_SIZE } from './constants';
import Tile from './Tile';

interface GameBoardProps {
  grid: (TileType | null)[][];
}

// Responsive cell sizing for 4x4 grid
// Smaller on desktop to ensure title stays visible
const getCellSize = () => {
  const GAP = 12;
  const GRID_SIZE = 4;

  // Calculate max available width (screen width - margins)
  // Use a safe margin of 20px on each side
  const maxBoardWidth = Math.min(window.innerWidth, window.screen.width) - 40;

  // Calculate max cell size that fits in the available width
  // Board width = (cell_size * grid_size) + (gap * (grid_size + 1))
  // max_cell_size = (max_board_width - (gap * (grid_size + 1))) / grid_size
  const maxCellSize = Math.floor((maxBoardWidth - (GAP * (GRID_SIZE + 1))) / GRID_SIZE);

  // Desktop: cap at 100px, Mobile: use larger percentage but constrained by max width
  const isDesktop = window.innerWidth >= 768;

  let size;
  if (isDesktop) {
    // Desktop: max(min(10vw, 10vh, 100px), 60px)
    const vw = window.innerWidth * 0.10; // 10vw for desktop
    const vh = window.innerHeight * 0.10; // 10vh for desktop
    size = Math.max(Math.min(vw, vh, 100), 60);
  } else {
    // Mobile: max(min(18vw, 14vh, 120px), 60px)
    const vw = window.innerWidth * 0.18; // 18vw for mobile
    const vh = window.innerHeight * 0.14; // 14vh for mobile
    size = Math.max(Math.min(vw, vh, 120), 40); // Lower min size to 40px for very small screens
  }

  // Ensure we never exceed the max cell size calculated from width
  return Math.min(size, maxCellSize);
};

const CELL_SIZE = getCellSize();
const GAP = 12;
const BOARD_SIZE = GRID_SIZE * CELL_SIZE + (GRID_SIZE + 1) * GAP;

const GameBoard: React.FC<GameBoardProps> = ({ grid }) => {
  const tiles = getAllTiles(grid);

  // Transparent board - stars show through from behind
  const boardStyle: React.CSSProperties = {
    position: 'relative',
    width: BOARD_SIZE,
    height: BOARD_SIZE,
    backgroundColor: 'transparent',
    borderRadius: 12,
    padding: 0,
    margin: '0 auto',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  };

  // Render empty cells - subtle outlines so stars show through
  const emptyCells = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      emptyCells.push(
        <div
          key={`cell-${row}-${col}`}
          style={{
            position: 'absolute',
            width: CELL_SIZE,
            height: CELL_SIZE,
            left: col * (CELL_SIZE + GAP) + GAP,
            top: row * (CELL_SIZE + GAP) + GAP,
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: 6,
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        />
      );
    }
  }

  return (
    <div className="alien2048-board" style={boardStyle}>
      {emptyCells}
      {tiles.map(tile => (
        <Tile
          key={tile.id}
          tile={tile}
          cellSize={CELL_SIZE}
          gap={GAP}
        />
      ))}
    </div>
  );
};

export default GameBoard;
